/** Super-admin: tenant list + provisioning modal. */
import { useCallback, useEffect, useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "../../api/http";
import * as api from "../../api/tenants";
import { CONTENT_LEVELS, TIERS, type Tenant } from "../../api/types";
import { useToast } from "../../components/ToastContext";
import { Badge, Button, Card, EmptyState, Input, Label, Modal, Select, Spinner } from "../../components/ui";
import { useT } from "../../i18n";

export function TenantsPage() {
  const t = useT();
  const { push } = useToast();
  const [tenants, setTenants] = useState<Tenant[] | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [inviteLink, setInviteLink] = useState<string | null>(null);
  const [emailed, setEmailed] = useState(true);
  const [levels, setLevels] = useState<string[]>([]);

  const [legalName, setLegalName] = useState("");
  const [adminEmail, setAdminEmail] = useState("");
  const [adminName, setAdminName] = useState("");
  const [tier, setTier] = useState("trial");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setTenants(await api.listTenants(true));
  }, []);

  useEffect(() => {
    reload().catch(() => setTenants([]));
  }, [reload]);

  const onProvision = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await api.createTenant({
        legal_name: legalName,
        subscription_tier: tier,
        admin_email: adminEmail,
        admin_full_name: adminName,
        content_levels: levels,
      });
      setInviteLink(result.invite_link);
      setEmailed(result.email_configured !== false);
      push("success", `${result.tenant.legal_name} → ${result.tenant.subdomain}`);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setInviteLink(null);
    setLevels([]);
    setLegalName("");
    setAdminEmail("");
    setAdminName("");
    setError(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-extrabold">{t("tenants.title")}</h1>
        <Button onClick={() => setShowModal(true)}>{t("tenants.provision")}</Button>
      </div>

      {tenants === null ? (
        <div className="grid place-items-center py-12">
          <Spinner size={28} />
        </div>
      ) : tenants.length === 0 ? (
        <Card>
          <EmptyState message="No tenants yet." />
        </Card>
      ) : (
        <Card>
          <div className="min-w-0 overflow-x-auto">
            <table className="w-full min-w-[34rem] text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase text-muted">
                  <th className="pb-2">{t("tenants.legalName")}</th>
                  <th className="pb-2">{t("tenants.subdomain")}</th>
                  <th className="pb-2">{t("tenants.tier")}</th>
                  <th className="pb-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {tenants.map((row) => (
                  <tr key={row.id} className="border-b border-line/60 last:border-0">
                    <td className="py-3">
                      <Link to={`/tenants/${row.id}`} className="font-semibold text-brand hover:underline">
                        {row.legal_name}
                      </Link>
                    </td>
                    <td className="py-3 text-muted">{row.subdomain}</td>
                    <td className="py-3">
                      <Badge tone="brand">{row.subscription_tier}</Badge>
                    </td>
                    <td className="py-3">
                      <Badge tone={row.is_active ? "success" : "danger"}>
                        {row.is_active ? t("tenants.active") : t("tenants.inactive")}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Modal open={showModal} onClose={closeModal} title={t("tenants.provision.title")}>
        {inviteLink ? (
          <div className="space-y-4">
            <p className="text-sm text-success">
              {emailed ? t("tenants.provisioned") : t("tenants.provisionedNoEmail")}
            </p>
            <code data-testid="invite-link" className="block break-all rounded-control bg-canvas p-3 text-xs">
              {inviteLink}
            </code>
            <Button onClick={closeModal} className="w-full">
              {t("common.close")}
            </Button>
          </div>
        ) : (
          <form onSubmit={onProvision} className="space-y-4">
            <div>
              <Label htmlFor="legalName">{t("tenants.legalName")}</Label>
              <Input id="legalName" required value={legalName} onChange={(e) => setLegalName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="tier">{t("tenants.tier")}</Label>
              <Select id="tier" value={tier} onChange={(e) => setTier(e.target.value)}>
                {TIERS.map((tierOption) => (
                  <option key={tierOption} value={tierOption}>
                    {tierOption}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="adminEmail">{t("tenants.adminEmail")}</Label>
              <Input
                id="adminEmail"
                type="email"
                required
                value={adminEmail}
                onChange={(e) => setAdminEmail(e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="adminName">{t("tenants.adminName")}</Label>
              <Input id="adminName" required value={adminName} onChange={(e) => setAdminName(e.target.value)} />
            </div>
            <div>
              <Label htmlFor="levels">{t("tenants.levels")}</Label>
              <p className="mb-2 text-xs text-muted">{t("tenants.levelsHint")}</p>
              <div id="levels" className="flex flex-wrap gap-2">
                {CONTENT_LEVELS.map((lvl) => {
                  const on = levels.includes(lvl);
                  return (
                    <button
                      key={lvl}
                      type="button"
                      aria-pressed={on}
                      onClick={() =>
                        setLevels((prev) =>
                          prev.includes(lvl) ? prev.filter((l) => l !== lvl) : [...prev, lvl],
                        )
                      }
                      className={`rounded-control border px-3 py-1.5 text-sm font-semibold ${
                        on
                          ? "border-brand bg-brand-soft text-brand-dark"
                          : "border-line text-muted hover:text-ink"
                      }`}
                    >
                      {lvl}
                    </button>
                  );
                })}
              </div>
              <div className="mt-2 flex gap-3 text-xs">
                <button
                  type="button"
                  onClick={() => setLevels([...CONTENT_LEVELS])}
                  className="font-semibold text-brand hover:underline"
                >
                  {t("tenants.levelsAll")}
                </button>
                <button
                  type="button"
                  onClick={() => setLevels([])}
                  className="font-semibold text-muted hover:underline"
                >
                  {t("tenants.levelsNone")}
                </button>
              </div>
            </div>
            {error && <p role="alert" className="text-sm text-danger">{error}</p>}
            <Button type="submit" loading={busy} className="w-full">
              {t("tenants.provision.submit")}
            </Button>
          </form>
        )}
      </Modal>
    </div>
  );
}
